import { create } from "zustand";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { listOrganizationsForUser } from "../lib/api/org";
import { useAuthStore } from "./auth";
import { useDevAuthStore } from "./devAuth";
import type { OrganizationMembership } from "../lib/api/types";

/**
 * The real, session-derived "current organization" state. Replaces
 * DevAuthPanel's manually-entered organization_id as the PRIMARY source
 * of which org the app operates against -- see
 * useCurrentOrganizationId() below, the hook every route should use
 * instead of reading useDevAuthStore directly.
 *
 * Not persisted itself: it's derived fresh each session from
 * listOrganizationsForUser (see useLoadCurrentOrgMemberships, wired at
 * the app root in main.tsx) once useAuthStore's user is known. Which
 * org is "current" among possibly-several is tracked here so a user
 * who switches between orgs mid-session keeps their selection.
 */
interface CurrentOrgState {
  memberships: OrganizationMembership[];
  selectedOrganizationId: string | null;
  setMemberships: (memberships: OrganizationMembership[]) => void;
  selectOrganization: (organizationId: string) => void;
  clear: () => void;
}

export const useCurrentOrgStore = create<CurrentOrgState>()((set, get) => ({
  memberships: [],
  selectedOrganizationId: null,
  setMemberships: (memberships) => {
    const current = get().selectedOrganizationId;
    const stillValid = current && memberships.some((m) => m.organization.id === current);
    set({
      memberships,
      // Default to the first membership (typically the org just
      // created at registration, or the sole org most users will ever
      // have) unless the user already had a different, still-valid
      // selection.
      selectedOrganizationId: stillValid ? current : (memberships[0]?.organization.id ?? null),
    });
  },
  selectOrganization: (organizationId) => set({ selectedOrganizationId: organizationId }),
  clear: () => set({ memberships: [], selectedOrganizationId: null }),
}));

/**
 * Fetches the logged-in user's real organization memberships and feeds
 * them into useCurrentOrgStore. Call this once, at the app root (see
 * main.tsx) -- NOT per-route -- so every route sees the same freshly
 * loaded state without each one re-fetching independently.
 */
export function useLoadCurrentOrgMemberships() {
  const user = useAuthStore((s) => s.user);
  const setMemberships = useCurrentOrgStore((s) => s.setMemberships);
  const clear = useCurrentOrgStore((s) => s.clear);

  const query = useQuery({
    queryKey: ["organizations-for-user", user?.id],
    queryFn: () => listOrganizationsForUser(user!.id),
    enabled: !!user,
  });

  useEffect(() => {
    if (!user) {
      clear();
      return;
    }
    if (query.data?.success) {
      setMemberships(query.data.memberships);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setMemberships/clear are stable zustand setters
  }, [user, query.data]);

  return query;
}

/**
 * The organization_id every route should actually use. Prefers the
 * real, session-derived current org; falls back to DevAuthPanel's
 * manually-entered value only when no real org is loaded yet (e.g. no
 * JWT_SECRET configured locally, see docs/decisions/ADR-002 -- the dev
 * panel remains a legitimate fallback for that specific case, not a
 * competing primary path).
 */
export function useCurrentOrganizationId(): string {
  const real = useCurrentOrgStore((s) => s.selectedOrganizationId);
  const dev = useDevAuthStore((s) => s.organizationId);
  return real || dev;
}
